import React, {
  useCallback,
  useEffect,
  useState,
  type ReactElement,
} from "react";

import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  ScatterController,
  TimeSeriesScale,
  Tooltip,
} from "chart.js";
import "chartjs-adapter-moment";
import annotationPlugin from "chartjs-plugin-annotation";
import zoomPlugin from "chartjs-plugin-zoom";
import Interweave from "interweave";
import { Provider as JotaiProvider } from "jotai";
import { useCookies } from "react-cookie";
import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import ReactDOM from "react-dom";
import {
  BrowserRouter,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { ToastProvider, useToasts } from "react-toast-notifications";
import { QueryParamProvider } from "use-query-params";
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6";
import { validate as isValidUUID } from "uuid";

import MaintenanceScreen from "@components/error-pages/maintenance-screen";
import { UserProvider } from "@components/user-context";
import { useInterval } from "@components/utilities";
import { ErrorBoundary } from "@components/v2/analytics";
import { useAppcuesBannerHeight } from "@hooks/appcues-banner-height";
import { ALLOWED_NON_PREFIXED_PATHS } from "@hooks/use-allowed-nonprefixed-page";
import { type User } from "@internal/user";
import { createTheme, MantineProvider } from "@mantine/core";
import "@mantine/core/styles.css";
import { Toast } from "@shared/toast";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import OAuthFinalizer from "@utils/oauth-finalizer";

import App from "./app";
import { Auth0Provider } from "./components/auth0-wrapper";
import ErrorScreen, {
  ErrorScreenBoundaryWithUserContext,
} from "./components/error-pages/error-screen";
import config from "./environment";
import * as serviceWorker from "./serviceWorker";
import "./styles/index.scss";
import { OAuth2Handler } from "./utils/oauth2-handler";

Chart.register(
  annotationPlugin,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  ScatterController,
  TimeSeriesScale,
  Filler,
  Tooltip,
  zoomPlugin,
  Legend,
);
Chart.defaults.font.family = "Commons Pro";
// interstellar+2
Chart.defaults.color = "#010614";

/* Even in development, this prints many many many messages. If you would like to re-enable,
   just uncomment.
   if (process.env.NODE_ENV === "development") {
     // eslint-disable-next-line @typescript-eslint/no-var-requires
     const whyDidYouRender = require("@welldone-software/why-did-you-render");
     whyDidYouRender(React, {
       trackAllPureComponents: true,
     });
   } */

const HomeHandler = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const appcuesBannerHeight = useAppcuesBannerHeight();

  const allowedNonPrefixedPaths = ALLOWED_NON_PREFIXED_PATHS.map(
    (path) => path.substring(1).split("/")[0],
  );
  const pathParams = pathname.split("/");
  let urlUserId = "";
  if (
    pathParams.length > 1 &&
    !allowedNonPrefixedPaths.includes(pathParams[1])
  ) {
    const potentialClientId = pathParams[1];
    if (isValidUUID(potentialClientId)) {
      urlUserId = potentialClientId;
    }
  }

  return (
    <main
      style={{ height: `calc(100vh - ${appcuesBannerHeight}px)` }}
      className="flex w-screen"
    >
      <JotaiProvider>
        <Auth0Provider
          domain={config.auth0Domain}
          clientId={config.auth0ClientId}
          authorizationParams={{
            audience: config.auth0Audience,
            redirect_uri: window.location.origin,
          }}
          onRedirectCallback={(appState: any) => {
            const path =
              (appState && appState.targetUrl) || window.location.pathname;
            navigate(path);
          }}
        >
          <UserProvider urlUserId={urlUserId}>
            <ErrorBoundary
              FallbackComponent={ErrorScreenBoundaryWithUserContext}
            >
              <Routes>
                <Route
                  path="/callbacksV2/:integration"
                  element={<OAuthFinalizer />}
                />
                <Route path="/:userId/*" element={<App />} />
              </Routes>
            </ErrorBoundary>
          </UserProvider>
        </Auth0Provider>
      </JotaiProvider>
    </main>
  );
};

const theme = createTheme({});

const queryClient = new QueryClient();
const Main = () => (
  <MantineProvider theme={theme}>
    <ToastProvider
      placement="bottom-center"
      autoDismiss={true}
      components={{ Toast }}
    >
      <MaintenanceBoundary>
        <QueryClientProvider client={queryClient}>
          <ErrorBoundary FallbackComponent={ErrorScreen}>
            <BrowserRouter>
              <QueryParamProvider
                adapter={ReactRouter6Adapter}
                options={{
                  enableBatching: true,
                  updateType: "replaceIn",
                  removeDefaultsFromUrl: true,
                }}
              >
                <Routes>
                  <Route path="/callbacks/*" element={<OAuth2Handler />} />
                  <Route
                    path="/shopify-login-callback"
                    element={<OAuth2Handler />}
                  />
                  <Route path="/*" element={<HomeHandler />} />
                </Routes>
              </QueryParamProvider>
            </BrowserRouter>
          </ErrorBoundary>
        </QueryClientProvider>
      </MaintenanceBoundary>
    </ToastProvider>
  </MantineProvider>
);
interface MaintenanceBoundaryProps {
  children: ReactElement | ReactElement[];
  userData?: User;
}

interface LoadingPageAppStatusNotice {
  errorMessage?: string;
  infoMessage?: string;
  blockLogin: boolean;
  isDismissable: boolean;
  isClientSpecific: boolean;
  maintencePageTitle: string;
  maintencePageDescription: string;
}

const ONE_HOUR = 60 * 60 * 1000;
const MAINTENANCE_CHECK_INTERVAL = 60 * 1000; // 1 minutes
const MAINTENANCE_COOKIE_NAME = "bannerDismissAt";
const MAINTENANCE_STATUS_ENDPOINT_PROD =
  "https://storage.googleapis.com/storage/v1/b/static.northbeam.io/o/loading_page_app_status_notice.jsonc?alt=media";
const MAINTENANCE_STATUS_ENDPOINT_CANARY =
  "https://storage.googleapis.com/storage/v1/b/static.northbeam.io/o/loading_page_app_status_notice_canary.jsonc?alt=media";

const stringHash = (str: string) => {
  return str.split("").reduce((a, b) => {
    a = (a << 5) - a + b.charCodeAt(0);
    return a & a;
  }, 0);
};

export function MaintenanceBoundary({
  children,
  userData,
}: MaintenanceBoundaryProps) {
  const [canLogin, setCanLogin] = useState(true);
  const [maintenanceScreenText, setMaintenanceScreenText] = useState<{
    maintenanceTitle?: string;
    maintenanceDescription?: string;
  }>();
  const { addToast, removeToast } = useToasts();
  const [cookies, setCookie] = useCookies<string>([MAINTENANCE_COOKIE_NAME]);

  const checkMaintenanceBanner = useCallback(() => {
    // prevent caching by using timestamp in url param
    const timestamp = new Date().getTime();

    const endpointUrl = config.isNBInternal
      ? MAINTENANCE_STATUS_ENDPOINT_CANARY
      : MAINTENANCE_STATUS_ENDPOINT_PROD;

    fetch(`${endpointUrl}&ts=${timestamp}`)
      .then<LoadingPageAppStatusNotice | null>((res) => res.json())
      .then((siteStatus) => {
        const hash = stringHash(JSON.stringify(siteStatus)).toString();

        const onBannerDismiss = () =>
          setCookie(
            MAINTENANCE_COOKIE_NAME,
            `${hash}__${new Date().getTime()}`,
            {
              path: "/",
            },
          );

        let displayBanner = false;

        const [storedHash, storedTimestamp] = (
          cookies[MAINTENANCE_COOKIE_NAME] || ""
        ).split("__");
        const bannerDismissAt = Number(storedTimestamp || 0);
        const currentTime = new Date().getTime();
        const difference = currentTime - bannerDismissAt;

        if (
          storedHash !== hash ||
          (storedHash === hash && difference > ONE_HOUR)
        ) {
          displayBanner = true;
        }

        removeToast("maintenance-warning");
        removeToast("maintenance-info");

        setMaintenanceScreenText({
          maintenanceTitle: siteStatus?.maintencePageTitle,
          maintenanceDescription: siteStatus?.maintencePageDescription,
        });

        // putting it out into two conditionals for clarity
        if (
          (siteStatus?.isClientSpecific && !userData) ||
          (siteStatus?.isClientSpecific &&
            !userData?.featureFlags?.clientSpecificIncidentBanner)
        ) {
          // do nothing
          return;
        }

        if (displayBanner && siteStatus?.errorMessage) {
          addToast(
            <div>
              <Interweave content={siteStatus.errorMessage} />
            </div>,
            {
              id: "maintenance-warning",
              appearance: "warning",
              autoDismiss: false,
              onDismiss: onBannerDismiss,
              blockDismiss: !siteStatus?.isDismissable,
            },
          );
        }

        if (siteStatus?.infoMessage) {
          if (displayBanner && siteStatus?.infoMessage) {
            addToast(
              <div>
                <Interweave content={siteStatus.infoMessage} />
              </div>,
              {
                id: "maintenance-info",
                appearance: "info",
                autoDismiss: false,
                onDismiss: onBannerDismiss,
                blockDismiss: !siteStatus?.isDismissable,
              },
            );
          }
        }

        if (siteStatus?.blockLogin) {
          setCanLogin(false);
        }
      })
      .catch(() => {
        // Do nothing
      });
  }, [cookies, removeToast, userData, setCookie, addToast]);

  useEffect(checkMaintenanceBanner, [checkMaintenanceBanner]);
  useInterval(checkMaintenanceBanner, MAINTENANCE_CHECK_INTERVAL);

  return (
    <>
      {canLogin ? (
        children
      ) : (
        <MaintenanceScreen
          title={maintenanceScreenText?.maintenanceTitle}
          description={maintenanceScreenText?.maintenanceDescription}
        />
      )}
    </>
  );
}

ReactDOM.render(<Main />, document.getElementById("root"));

/* If you want your app to work offline and load faster, you can change
   unregister() to register() below. Note this comes with some pitfalls.
   Learn more about service workers: https://bit.ly/CRA-PWA */
serviceWorker.unregister();
